import { MapPin } from "lucide-react";
import React, { useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ThemedSelect } from "@/components/ui/ThemedSelect";
import { API } from "@/config";
import { AddressAutocomplete } from "./AddressAutocomplete";

/**
 * LocationPicker — the Disney-Resort vs Off-site/Airbnb toggle plus the
 * resort dropdown / area dropdown / address entry that unfolds beneath it.
 *
 * The same UI serves BOTH the delivery destination and the (opt-in) split
 * pickup destination. Callers pass:
 *  - `state` object with { isOffProperty, zoneId, resort, address, addrCheck }
 *  - `set*` setters for each of those fields
 *  - `testidPrefix` — 'trip-setup' for delivery, 'different-pickup' for pickup
 *  - `labels` object for the two toggle-button placeholder texts
 *  - `zones` list from the site config
 *  - `verb` — "deliver" or "pick up" — used inside the address-check toast
 *  - `defaultDisneyZoneId` — usually "wdw-core"
 */
export const LocationPicker = ({
  state,
  setIsOffProperty,
  setZoneId,
  setResort,
  setAddress,
  setAddrCheck,
  zones,
  testidPrefix,
  labels,
  verb = "deliver",
  defaultDisneyZoneId = "wdw-core",
}) => {
  const { isOffProperty, zoneId, resort, address, addrCheck } = state;
  const [checking, setChecking] = React.useState(false);
  const activeZone = zones.find((z) => z.id === zoneId);
  const offsiteAreaOptions = useMemo(() => zones.filter((z) => !z.disney).map((z) => ({
    value: z.id,
    label: `${z.area}${z.delivery_fee > 0 ? ` — +$${z.delivery_fee.toFixed(2)}` : ""}`,
  })), [zones]);
  const needsAddress = activeZone?.requires_address;
  const needsResort = !isOffProperty || (activeZone && !activeZone.requires_address);

  const pickDisney = () => {
    setIsOffProperty(false);
    const wdw = zones.find((z) => z.id === defaultDisneyZoneId);
    setZoneId(wdw ? wdw.id : "");
    setResort(""); setAddress(""); setAddrCheck(null);
  };
  const pickOffsite = () => {
    setIsOffProperty(true); setZoneId(""); setResort(""); setAddress(""); setAddrCheck(null);
  };

  const checkAddress = async (place) => {
    const addr = place?.formatted || address;
    if (!addr.trim()) return toast.error(`Enter an address to check`);
    setChecking(true); setAddrCheck(null);
    try {
      const body = place?.lat != null && place?.lng != null ? { address: addr, lat: place.lat, lng: place.lng } : { address: addr };
      if (zoneId) body.zone_id = zoneId;
      const res = await axios.post(`${API}/check-address`, body);
      setAddrCheck(res.data);
      if (res.data.delivers && res.data.zones.length > 0) {
        setZoneId(res.data.zones[0].id);
        toast.success(res.data.approved_location ? `Great — ${res.data.approved_location.name} is an approved location!` : verb === "pick up" ? "Great — we pick up from there!" : "Great — we deliver here!");
      }
    } catch (e) {
      setAddrCheck({ delivers: false, reason: "We couldn't verify that address. Please select an address from the suggestions in the dropdown." });
      toast.error(e.response?.data?.detail || "Address lookup failed. Try again.");
    } finally { setChecking(false); }
  };

  return (
    <>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={pickDisney}
          className={`flex-1 py-2 rounded-lg text-sm border transition-colors ${!isOffProperty ? "bg-[#FFD166]/15 border-[#FFD166] text-[#FFD166]" : "border-[#FFD166]/25 text-[#B9C8D5]"}`}
          data-testid={`${testidPrefix}-dest-disney`}
        >Disney Resort</button>
        <button
          type="button"
          onClick={pickOffsite}
          className={`flex-1 py-2 rounded-lg text-sm border transition-colors ${isOffProperty ? "bg-[#FFD166]/15 border-[#FFD166] text-[#FFD166]" : "border-[#FFD166]/25 text-[#B9C8D5]"}`}
          data-testid={`${testidPrefix}-dest-offsite`}
        >Off-site / Airbnb</button>
      </div>

      {!isOffProperty ? (
        <ThemedSelect
          value={resort}
          onChange={setResort}
          placeholder={labels.disneyPlaceholder}
          options={(zones.find((z) => z.id === defaultDisneyZoneId)?.resorts || []).map((r) => ({ value: r, label: r }))}
          data-testid={`${testidPrefix}-resort`}
        />
      ) : (
        <>
          <ThemedSelect
            value={zoneId}
            onChange={(nextId) => {
              const nextZone = zones.find((z) => z.id === nextId);
              const prevZone = zones.find((z) => z.id === zoneId);
              setZoneId(nextId); setResort("");
              if (!(nextZone?.requires_address && prevZone?.requires_address)) setAddress("");
              setAddrCheck(null);
            }}
            placeholder={labels.areaPlaceholder}
            options={offsiteAreaOptions}
            data-testid={`${testidPrefix}-offsite-area`}
          />
          {zoneId && needsAddress && (
            <div className="mt-3">
              <div className="flex gap-2">
                <AddressAutocomplete
                  name={`${testidPrefix}-address`}
                  placeholder={labels.addressPlaceholder}
                  value={address}
                  onChange={(v) => { setAddress(v); setAddrCheck(null); }}
                  onSelect={(place) => { if (place) checkAddress(place); }}
                  testid={`${testidPrefix}-address`}
                />
                <button type="button" onClick={() => checkAddress()} disabled={checking} className="btn-ghost shrink-0 disabled:opacity-60" data-testid={`${testidPrefix}-check-address`}>
                  {checking ? "Checking…" : "Check"}
                </button>
              </div>
              {addrCheck && (
                <div className={`mt-3 text-sm p-3 rounded-lg border ${addrCheck.delivers ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`} data-testid={`${testidPrefix}-address-result`}>
                  {addrCheck.delivers ? (addrCheck.approved_location ? `✓ ${addrCheck.approved_location.name} — approved delivery location` : labels.successAddress) : `✗ ${addrCheck.reason || labels.failAddress}`}
                </div>
              )}
            </div>
          )}
          {zoneId && !needsAddress && (
            <div className="mt-3">
              <ThemedSelect
                value={resort}
                onChange={setResort}
                placeholder={labels.offsiteResortPlaceholder}
                options={(activeZone?.resorts || []).map((r) => ({ value: r, label: r }))}
                data-testid={`${testidPrefix}-resort`}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

/** Compute whether a location is fully specified — mirrors old `destinationSet`. */
export const isLocationSet = (state, zones) => {
  const { isOffProperty, zoneId, resort, address, addrCheck } = state;
  if (isOffProperty) {
    if (!zoneId) return false;
    const z = zones.find((zz) => zz.id === zoneId);
    if (z?.requires_address) return !!addrCheck?.delivers;
    return !!resort;
  }
  return !!resort;
};

/** Whether the location requires an in-person lobby meet (Disney resort, or a
 *  meet-only hotel like Cabana Bay / Endless Summer) — gates the confirmation checkbox. */
export const isDisneyLocation = (state, zones = []) => {
  if (!state.resort) return false;
  if (!state.isOffProperty) return true;
  const zone = zones.find((z) => z.id === state.zoneId);
  return !!(zone?.meet_only_resorts || []).includes(state.resort);
};

export default LocationPicker;
